import { compose } from 'recompose';

// lodash
import _replace from 'lodash/replace';
import _split from 'lodash/split';
import _filter from 'lodash/filter';
import _size from 'lodash/size';
import _reduce from 'lodash/reduce';
import _toString from 'lodash/toString';
import _toNumber from 'lodash/toNumber';

// utils/helpers
import Process from '@tekion/tekion-base/utils/Process';
import isStringEmpty from '@tekion/tekion-base/utils/isStringEmpty';
import { getFormattedNumberWithCommas } from './decimalInput.formatter';

// constants
import { CURRENCY_VALUE_PRECISION } from '../decimalInput.constants';
export var getPrecision = function getPrecision(value) {
  return _size(_split(value, '.')[1]);
};
var isDecimalCharacter = function isDecimalCharacter(_char) {
  return _char === '.';
};
var removeInvalidAmountCharacters = function removeInvalidAmountCharacters(value) {
  return _replace(value, /[^0-9.-]/g, '');
};
var isPrecisionGreaterThanCurrencyPrecision = function isPrecisionGreaterThanCurrencyPrecision(amountValueInString) {
  return getPrecision(amountValueInString) > CURRENCY_VALUE_PRECISION;
};

/**
 * This function handle the case of any invalid amount character being added such a-z or any special character except for ',' and '.'. e.g. 12,34q5.45
 * This function returns the amount value with these values removed and then shifts the cursor position by the number of characters removed.
 * valueWithMinus consists of all the invalid characters and amountValueInString is filtered of all these invalid values.
 */
var handleInvalidAmountCharacters = function handleInvalidAmountCharacters(next, params) {
  var amountValueInString = params.amountValueInString,
    shiftCursorPosition = params.shiftCursorPosition,
    valueWithMinus = params.valueWithMinus;
  var valueWithoutFormatting = _replace(valueWithMinus, /[,]/g, '');
  var sizeOfAmountValue = _size(amountValueInString);
  var sizeOfValueWithoutFormatting = _size(valueWithoutFormatting);
  if (sizeOfAmountValue !== sizeOfValueWithoutFormatting) {
    shiftCursorPosition(sizeOfAmountValue - sizeOfValueWithoutFormatting);
    return amountValueInString;
  }
  return next(params);
};

/**
 * If the user types in or removes ','. e.g. 1,23,4.50 or 1234.56
 * The behaviour is to retain the the position of cursor if user tries to remove ',' as it is the part of formatting and also not to allow the user to type in any new ','.
 */

var handleCommaEnteredOrRemoved = function handleCommaEnteredOrRemoved(next, params) {
  var amountValueInString = params.amountValueInString,
    shiftCursorPosition = params.shiftCursorPosition,
    valueWithMinus = params.valueWithMinus,
    prevValue = params.prevValue;
  var formattedAmount = getFormattedNumberWithCommas(amountValueInString);
  var noOfCommasInAmount = _reduce(formattedAmount, incrementIfComma, 0);
  var noOfCommasInInputValue = _reduce(_toString(valueWithMinus), incrementIfComma, 0);
  if (noOfCommasInInputValue !== noOfCommasInAmount && _toNumber(amountValueInString) === _toNumber(prevValue)) {
    // If the use removes the comma we need to shift the position to right by 1 to keep it the same as previous position as ',' is not allowed to be removed.
    shiftCursorPosition(noOfCommasInAmount - noOfCommasInInputValue);
    return amountValueInString;
  }
  return next(params);
};
var getNoOfDecimals = compose(_size, _filter);

/**
 * handles the case if users tries to type more than one decimal point. (e.g. 1,234.5.6)
 * the behaviour we have is we ignore the second typed decimal point
 */
var handleMultipleDecimalPoints = function handleMultipleDecimalPoints(next, params) {
  var amountValueInString = params.amountValueInString,
    prevPrecision = params.prevPrecision,
    shiftCursorPosition = params.shiftCursorPosition;
  var noOfDecimals = getNoOfDecimals(amountValueInString, isDecimalCharacter);
  if (noOfDecimals > 1) {
    var numericValueWithoutPrecision = _replace(amountValueInString, /[.]/g, '');
    var valueLength = _size(numericValueWithoutPrecision);
    var valueBeforeDecimal = numericValueWithoutPrecision.slice(0, valueLength - prevPrecision) || '';
    var valueAfterDecimal = numericValueWithoutPrecision.slice(valueLength - prevPrecision) || '';
    // we shift the cursor by number of decimals that are removed in order to retain the same position after removal
    shiftCursorPosition(1 - noOfDecimals);
    return "".concat(valueBeforeDecimal, ".").concat(valueAfterDecimal);
  }
  return next(params);
};
var getValueToShowAfterDecimal = function getValueToShowAfterDecimal(valueSplittedOnDecimal) {
  var valueAfterDecimal = valueSplittedOnDecimal[1] || '';
  var valueToShowAfterDecimal = valueAfterDecimal.slice(0, CURRENCY_VALUE_PRECISION);
  return valueToShowAfterDecimal;
};
var getValueBeforeDecimal = function getValueBeforeDecimal(value) {
  var valueSplittedOnDecimal = _split(value, '.');
  return valueSplittedOnDecimal[0];
};
var incrementIfComma = function incrementIfComma(acc, _char2) {
  if (_char2 === ',') {
    return acc + 1;
  }
  return acc;
};
var handleCursorPosition = function handleCursorPosition(_ref) {
  var valueWithMinus = _ref.valueWithMinus,
    amountValueInString = _ref.amountValueInString,
    shiftCursorPosition = _ref.shiftCursorPosition;
  var prevFormattedValueBeforeDecimal = getValueBeforeDecimal(valueWithMinus);
  var valueBeforeDecimal = getValueBeforeDecimal(amountValueInString);
  var formattedValueBeforeDecimal = getFormattedNumberWithCommas(valueBeforeDecimal);
  var commasBeforeChange = _reduce(prevFormattedValueBeforeDecimal, incrementIfComma, 0);
  var commasAfterChange = _reduce(formattedValueBeforeDecimal, incrementIfComma, 0);
  shiftCursorPosition(commasAfterChange - commasBeforeChange);
};

/**
 * This function handles the case if the precision of the number goes beyond 2.
 */
var handleCurrencyPrecision = function handleCurrencyPrecision(next, params) {
  var amountValueInString = params.amountValueInString,
    shiftCursorPosition = params.shiftCursorPosition,
    prevPrecision = params.prevPrecision,
    valueWithMinus = params.valueWithMinus;
  if (isPrecisionGreaterThanCurrencyPrecision(amountValueInString)) {
    // This if condition handles the case if the user types in a number after the 2 decimals on in between of the two decimal. We need to remove the least precision digit from the decimal number.
    if (prevPrecision === 2 && getPrecision(amountValueInString) === 3) {
      shiftCursorPosition();
      return amountValueInString.slice(0, -1);
    }
    var valueSplittedOnDecimal = _split(amountValueInString, '.');
    var valueBeforeDecimal = valueSplittedOnDecimal[0] || '';
    var valueToShowAfterDecimal = getValueToShowAfterDecimal(valueSplittedOnDecimal);
    handleCursorPosition({
      valueWithMinus: valueWithMinus,
      amountValueInString: amountValueInString,
      shiftCursorPosition: shiftCursorPosition
    });
    return "".concat(valueBeforeDecimal, ".").concat(valueToShowAfterDecimal);
  }
  return next(params);
};
var handleDefaultCase = function handleDefaultCase(next, params) {
  var amountValueInString = params.amountValueInString,
    valueWithMinus = params.valueWithMinus,
    shiftCursorPosition = params.shiftCursorPosition;
  handleCursorPosition({
    valueWithMinus: valueWithMinus,
    amountValueInString: amountValueInString,
    shiftCursorPosition: shiftCursorPosition
  });
  return amountValueInString;
};
var incrementIfMinus = function incrementIfMinus(acc, _char3) {
  if (_char3 === '-') {
    return acc + 1;
  }
  return acc;
};

/**
 * handle the case if user types or removes '-'
  */
var handleMinusCase = function handleMinusCase(next, params) {
  var valueFromInput = params.valueFromInput,
    shiftCursorPosition = params.shiftCursorPosition,
    amountValueInString = params.amountValueInString;
  var noOfMinus = _reduce("".concat(valueFromInput), incrementIfMinus, 0);
  if (noOfMinus > 1) {
    // if the user types two '-' we remove both so in that case we need to shift the position to the left by 2
    // the handling of minus is already done, this function only handles the cursor
    var areNoOfMinusEven = noOfMinus % 2 === 0;
    if (areNoOfMinusEven) {
      shiftCursorPosition(-noOfMinus);
    } else {
      shiftCursorPosition(-noOfMinus + 1);
    }
    return amountValueInString;
  }
  return next(params);
};
var handleNegativeValueAppended = function handleNegativeValueAppended(next, params) {
  var isNegativeValue = params.isNegativeValue,
    amountValueInString = params.amountValueInString,
    prevValue = params.prevValue,
    shiftCursorPosition = params.shiftCursorPosition;
  if (isNegativeValue && isStringEmpty(prevValue)) {
    shiftCursorPosition(2);
    return amountValueInString;
  }
  return next(params);
};
var handleNullValue = function handleNullValue(next, params) {
  var amountValueInString = params.amountValueInString;
  if (isStringEmpty(amountValueInString)) {
    return undefined;
  }
  return next(params);
};
var CURRENCY_VALUE_PARSING_PROCESS = new Process().addHandler(handleNullValue).addHandler(handleMultipleDecimalPoints).addHandler(handleCurrencyPrecision).addHandler(handleMinusCase).addHandler(handleInvalidAmountCharacters).addHandler(handleCommaEnteredOrRemoved).addHandler(handleNegativeValueAppended).addHandler(handleDefaultCase);
export var getParsedValue = function getParsedValue(_ref2) {
  var valueWithMinus = _ref2.valueWithMinus,
    prevPrecision = _ref2.prevPrecision,
    shiftCursorPosition = _ref2.shiftCursorPosition,
    prevValue = _ref2.prevValue,
    valueFromInput = _ref2.valueFromInput,
    isNegativeValue = _ref2.isNegativeValue;
  var amountValueInString = removeInvalidAmountCharacters(valueWithMinus);
  return CURRENCY_VALUE_PARSING_PROCESS.run({
    amountValueInString: amountValueInString,
    prevPrecision: prevPrecision,
    shiftCursorPosition: shiftCursorPosition,
    valueWithMinus: valueWithMinus,
    prevValue: prevValue,
    valueFromInput: valueFromInput,
    isNegativeValue: isNegativeValue
  });
};